import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navigation from './Navigation';
import api from '../config/api';
import './common.css';
import './ProductList.css';
import { View, Pause, Play, Trash } from 'grommet-icons';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
} from '@tanstack/react-table';

function ProductList() {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [rowSelection, setRowSelection] = useState({});
    const [sorting, setSorting] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');

    const fetchProducts = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await api.get('/api/products');
            if (response.data.success) {
                setProducts(response.data.data);
            }
        } catch (err) {
            if (err.response?.status === 401) {
                logout();
                navigate('/login');
            } else {
                setError('Failed to fetch products');
            }
            console.error('Error fetching products:', err);
        } finally {
            setLoading(false);
        }
    }, [logout, navigate]);

    useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    const handleDeleteProduct = useCallback(async (productId, productTitle) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete product "${productTitle}"? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            const response = await api.delete(`/api/products/${productId}`);
            if (response.data.success) {
                setSuccess('Product deleted successfully');
                fetchProducts();
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete product');
            setTimeout(() => setError(''), 3000);
        }
    }, [fetchProducts]);

    const handleTogglePause = useCallback(async (productId, currentPaused) => {
        try {
            const response = await api.put(`/api/products/${productId}`, {
                paused: !currentPaused
            });
            if (response.data.success) {
                setSuccess(`Product ${!currentPaused ? 'paused' : 'unpaused'} successfully`);
                fetchProducts();
                setTimeout(() => setSuccess(''), 3000);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update product');
            setTimeout(() => setError(''), 3000);
        }
    }, [fetchProducts]);

    const handleDeleteSelected = async () => {
        const selectedIds = Object.keys(rowSelection).filter(key => rowSelection[key]).map(key => products[parseInt(key)].id);

        if (selectedIds.length === 0) {
            setError('Please select at least one product to delete');
            setTimeout(() => setError(''), 3000);
            return;
        }

        const confirmed = window.confirm(
            `Are you sure you want to delete ${selectedIds.length} product(s)? This action cannot be undone.`
        );

        if (!confirmed) return;

        try {
            const deletePromises = selectedIds.map(productId =>
                api.delete(`/api/products/${productId}`)
            );

            await Promise.all(deletePromises);

            setSuccess(`${selectedIds.length} product(s) deleted successfully`);
            setRowSelection({});
            fetchProducts();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to delete products');
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleExportCSV = () => {
        if (products.length === 0) {
            setError('No products to export');
            setTimeout(() => setError(''), 3000);
            return;
        }

        // Define CSV headers
        const headers = [
            'ID', 'Title', 'eBay ID', 'eBay Variation SKU', 'Amazon SKU',
            'Price', 'Quantity', 'Seller', 'Shipping Cost', 'Shipping Time',
            'Status', 'Paused', 'eBay Product URL', 'Created'
        ];

        // Convert products to CSV rows
        const csvRows = products.map(product => [
            product.id,
            `"${(product.title || '').replace(/"/g, '""')}"`, // Escape quotes in title
            product.ebay_id || '',
            product.ebay_variation_sku || '',
            product.amazon_sku || '',
            product.price || '',
            product.quantity || '',
            product.seller_id || '',
            product.shipping_cost || '',
            product.shipping_time || '',
            product.listing_status || '',
            product.paused ? 'Yes' : 'No',
            product.ebay_product_url || '',
            product.created_at ? new Date(product.created_at).toLocaleString() : ''
        ]);

        // Combine headers and rows
        const csvContent = [
            headers.join(','),
            ...csvRows.map(row => row.join(','))
        ].join('\n');

        // Create blob and download
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `products_${new Date().toISOString().slice(0, 10)}.csv`);
        link.style.visibility = 'hidden';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setSuccess(`Exported ${products.length} products to CSV`);
        setTimeout(() => setSuccess(''), 3000);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString();
    };

    const formatPrice = (price) => {
        if (!price) return 'N/A';
        return `$${parseFloat(price).toFixed(2)}`;
    };

    const formatRelativeTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) return 'N/A';

        const now = new Date();
        const diffMs = date - now;
        const diffSeconds = Math.round(diffMs / 1000);

        const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });
        const ranges = [
            { unit: 'year', seconds: 60 * 60 * 24 * 365 },
            { unit: 'month', seconds: 60 * 60 * 24 * 30 },
            { unit: 'week', seconds: 60 * 60 * 24 * 7 },
            { unit: 'day', seconds: 60 * 60 * 24 },
            { unit: 'hour', seconds: 60 * 60 },
            { unit: 'minute', seconds: 60 },
            { unit: 'second', seconds: 1 },
        ];

        for (const range of ranges) {
            if (Math.abs(diffSeconds) >= range.seconds || range.unit === 'second') {
                const value = Math.round(diffSeconds / range.seconds);
                return rtf.format(value, range.unit);
            }
        }

        return 'N/A';
    };

    // Define table columns
    const columns = useMemo(() => [
        {
            id: 'select',
            header: ({ table }) => (
                <input
                    type="checkbox"
                    checked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    onChange={table.getToggleAllRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <input
                    type="checkbox"
                    checked={row.getIsSelected()}
                    disabled={!row.getCanSelect()}
                    onChange={row.getToggleSelectedHandler()}
                />
            ),
            enableSorting: false,
            enableGlobalFilter: false,
        },
        {
            accessorKey: 'id',
            header: 'ID',
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'title',
            header: 'Title',
            cell: info => (
                <span className="product-title" title={info.getValue()}>
                    {info.getValue() || 'N/A'}
                </span>
            ),
        },
        {
            accessorKey: 'ebay_id',
            header: 'eBay ID',
            cell: info => (
                <a
                    href={`https://www.ebay.com/itm/${info.getValue()}`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {`https://www.ebay.com/itm/${info.getValue()}`}
                </a>
            ),
        },
        {
            accessorKey: 'amazon_sku',
            header: 'Amazon SKU',
            cell: info => info.getValue() || 'N/A',
        },
        {
            accessorKey: 'price',
            header: 'Price',
            cell: info => formatPrice(info.getValue()),
        },
        {
            accessorKey: 'quantity',
            header: 'Quan.',
            cell: info => info.getValue() || 'N/A',
        },
        {
            accessorKey: 'seller_id',
            header: 'Seller',
            cell: info => info.getValue() || 'N/A',
        },
        {
            accessorKey: 'listing_status',
            header: 'Status',
            cell: info => (
                <span className={`status-badge ${info.getValue()?.toLowerCase() || 'unknown'}`}>
                    {info.getValue() || 'UNKNOWN'}
                </span>
            ),
        },
        {
            accessorKey: 'paused',
            header: 'Paused',
            cell: info => (
                <span className={`pause-badge ${info.getValue() ? 'paused' : 'active'}`}>
                    {info.getValue() ? 'Yes' : 'No'}
                </span>
            ),
        },
        {
            accessorKey: 'last_amazon_update_at',
            header: 'Amz Update',
            cell: info => formatRelativeTime(info.getValue()),
        },
        {
            accessorKey: 'created_at',
            header: 'Created',
            cell: info => (
                <span title={formatDate(info.getValue())}>
                    {formatRelativeTime(info.getValue())}
                </span>
            ),
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const product = row.original;
                return (
                    <div className="actions">
                        <button
                            className="btn-icon btn-view"
                            onClick={() => navigate(`/products/${product.id}`)}
                            title="View Details"
                        >
                            <View size="16px" />
                        </button>
                        <button
                            className="btn-icon btn-pause"
                            onClick={() => handleTogglePause(product.id, product.paused)}
                            title={product.paused ? 'Unpause' : 'Pause'}
                        >
                            {product.paused ? <Play size="16px" /> : <Pause size="16px" />}
                        </button>
                        <button
                            className="btn-icon btn-delete"
                            onClick={() => handleDeleteProduct(product.id, product.title)}
                            title="Delete Product"
                        >
                            <Trash size="16px" />
                        </button>
                    </div>
                );
            },
            enableSorting: false,
            enableGlobalFilter: false,
        },
    ], [navigate, handleDeleteProduct, handleTogglePause]);

    const table = useReactTable({
        data: products,
        columns,
        state: {
            rowSelection,
            sorting,
            globalFilter,
        },
        enableRowSelection: true,
        onRowSelectionChange: setRowSelection,
        onSortingChange: setSorting,
        onGlobalFilterChange: setGlobalFilter,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        initialState: {
            pagination: {
                pageSize: 10,
            },
        },
    });

    const selectedCount = Object.keys(rowSelection).filter(key => rowSelection[key]).length;

    return (
        <div className="page-container">
            <Navigation />

            <div className="page-content">
                <div className="page-header">
                    <h2>Products</h2>
                    <div className="header-actions">
                        {selectedCount > 0 && (
                            <button
                                className="btn btn-danger"
                                onClick={handleDeleteSelected}
                            >
                                Delete Selected ({selectedCount})
                            </button>
                        )}
                        <button
                            className="btn btn-success"
                            onClick={handleExportCSV}
                        >
                            Export CSV
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/products/add')}
                        >
                            + Add Product
                        </button>
                    </div>
                </div>

                {error && <div className="alert alert-error">{error}</div>}
                {success && <div className="alert alert-success">{success}</div>}

                {loading ? (
                    <div className="loading">Loading products...</div>
                ) : (
                    <>
                        {/* Search Box */}
                        <div className="table-controls">
                            <div className="search-box">
                                <input
                                    type="text"
                                    value={globalFilter ?? ''}
                                    onChange={e => setGlobalFilter(e.target.value)}
                                    placeholder="Search all columns..."
                                    className="search-input"
                                />
                            </div>
                            <div className="page-size-selector">
                                <label>Show: </label>
                                <select
                                    value={table.getState().pagination.pageSize}
                                    onChange={e => {
                                        table.setPageSize(Number(e.target.value))
                                    }}
                                >
                                    {[10, 20, 30, 50, 100].map(pageSize => (
                                        <option key={pageSize} value={pageSize}>
                                            {pageSize}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="table-container">
                            <table className="products-table">
                                <thead>
                                    {table.getHeaderGroups().map(headerGroup => (
                                        <tr key={headerGroup.id}>
                                            {headerGroup.headers.map(header => (
                                                <th key={header.id} colSpan={header.colSpan}>
                                                    {header.isPlaceholder ? null : (
                                                        <div
                                                            className={
                                                                header.column.getCanSort()
                                                                    ? 'sortable-header'
                                                                    : ''
                                                            }
                                                            onClick={header.column.getToggleSortingHandler()}
                                                        >
                                                            {flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                            {header.column.getCanSort() && (
                                                                <span className="sort-indicator">
                                                                    {{
                                                                        asc: ' 🔼',
                                                                        desc: ' 🔽',
                                                                    }[header.column.getIsSorted()] ?? ' ⬍'}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={columns.length} className="no-data">
                                                {globalFilter ? 'No products match your search' : 'No products found. Add your first product!'}
                                            </td>
                                        </tr>
                                    ) : (
                                        table.getRowModel().rows.map(row => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map(cell => (
                                                    <td key={cell.id}>
                                                        {flexRender(
                                                            cell.column.columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="pagination-controls">
                            <div className="pagination-info">
                                Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
                                {Math.min(
                                    (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                                    table.getFilteredRowModel().rows.length
                                )}{' '}
                                of {table.getFilteredRowModel().rows.length} products
                            </div>
                            <div className="pagination-buttons">
                                <button
                                    className="btn btn-small"
                                    onClick={() => table.setPageIndex(0)}
                                    disabled={!table.getCanPreviousPage()}
                                >
                                    {'<<'}
                                </button>
                                <button
                                    className="btn btn-small"
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage()}
                                >
                                    {'<'}
                                </button>
                                <span className="page-indicator">
                                    Page{' '}
                                    <strong>
                                        {table.getState().pagination.pageIndex + 1} of{' '}
                                        {table.getPageCount()}
                                    </strong>
                                </span>
                                <button
                                    className="btn btn-small"
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage()}
                                >
                                    {'>'}
                                </button>
                                <button
                                    className="btn btn-small"
                                    onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                                    disabled={!table.getCanNextPage()}
                                >
                                    {'>>'}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default ProductList;
